import { useDetailRecord } from "@taunggyi-plus/auth";
import { RecordFormDialog } from "@taunggyi-plus/design-system";
import { useOrderRefresh } from "@taunggyi-plus/auth";
import { useFeedback } from "@taunggyi-plus/auth";
import { ProviderReputation } from "@taunggyi-plus/finance-feature";
import { DirectDeliveryForm } from "./direct-delivery";
import { useCallback, useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { useAuth } from "@taunggyi-plus/auth";
import {
  Card,
  ManagementListToolbar,
  PaginatedList,
  StatusPill,
} from "@taunggyi-plus/design-system";
import type { Locale } from "@taunggyi-plus/domain";
type Job = {
  id: string;
  status: string;
  pickup_address: string;
  dropoff_address: string;
  recipient_name: string;
  recipient_phone: string;
  cash_to_collect_minor: number;
  rider_id: string | null;
};
type Fleet = {
  id: string;
  organization_id?: string;
  display_name: string;
  address?: string;
  phone?: string;
  is_active: boolean;
};
type Rider = {
  id: string;
  display_name: string;
  phone: string;
  vehicle_label: string;
  is_available: boolean;
  is_active: boolean;
};

const directoryWords = {
  en: {
    title: "Link Delivery",
    help: "Choose a verified local delivery operator and call to make a manual request.",
    verified: "Verified delivery",
    request: "Request delivery",
    empty: "No active delivery services are published yet.",
    close: "Close delivery details",
  },
  my: {
    title: "ပို့ဆောင်ရေးဝန်ဆောင်မှုများ",
    help: "အတည်ပြုထားသော ဒေသခံပို့ဆောင်ရေးကိုရွေးပြီး ကိုယ်တိုင်တောင်းဆိုရန် ဖုန်းခေါ်ပါ။",
    verified: "အတည်ပြုပို့ဆောင်ရေး",
    request: "ပို့ဆောင်မှု တောင်းဆိုမည်",
    empty: "ဖွင့်ထားသော ပို့ဆောင်ရေးဝန်ဆောင်မှု မရှိသေးပါ။",
    close: "ပို့ဆောင်ရေးအသေးစိတ် ပိတ်မည်",
  },
  shn: {
    title: "ဝန်ဆောင်ႈသူင်ႇၶူဝ်း",
    help: "လိူၵ်ႈၽူႈသူင်ႇဢၼ်ယိုၼ်ယၼ်ဝႆႉသေ ႁွင်ႉၾူၼ်းယွၼ်းသူင်ႇ။",
    verified: "ယိုၼ်ယၼ်ယဝ်ႉ",
    request: "ယွၼ်းသူင်ႇၶူဝ်း",
    empty: "ပႆႇမီးဝန်ဆောင်ႈသူင်ႇဢၼ်ပိုတ်ႇဝႆႉ။",
    close: "ပိၵ်ႉၶေႃႈမုၼ်းသူင်ႇၶူဝ်း",
  },
  zh: {
    title: "配送服务",
    help: "选择经过认证的本地配送运营商，并致电提出人工配送申请。",
    verified: "认证配送",
    request: "申请配送",
    empty: "暂无已发布的配送服务。",
    close: "关闭配送详情",
  },
} as const;

export function CustomerDeliveryDirectory({
  locale,
  renderAuthPrompt,
  variant = "directory",
  limit,
  searchQuery = "",
  onResultCount,
}: {
  locale: Locale;
  renderAuthPrompt?: (onClose: () => void) => ReactNode;
  variant?: "directory" | "home";
  limit?: number;
  searchQuery?: string;
  onResultCount?: (count: number) => void;
}) {
  const auth = useAuth();
  const text = directoryWords[locale];
  const [fleets, setFleets] = useState<Fleet[]>([]);
  const [selected, setSelected] = useDetailRecord(fleets);
  const [loginRequested, setLoginRequested] = useState(false);
  useEffect(() => {
    if (!auth.client) return;
    let active = true;
    void auth.client
      .from("delivery_fleets")
      .select("id,organization_id,display_name,address,phone,is_active")
      .eq("is_active", true)
      .then(({ data }) => {
        if (active) setFleets((data ?? []) as Fleet[]);
      });
    return () => {
      active = false;
    };
  }, [auth.client]);
  const normalizedSearch = searchQuery.trim().toLocaleLowerCase();
  const matchingFleets = normalizedSearch
    ? fleets.filter((fleet) =>
        [fleet.display_name, fleet.address, fleet.phone, text.title, text.help]
          .join(" ")
          .toLocaleLowerCase()
          .includes(normalizedSearch),
      )
    : fleets;
  const visibleFleets = typeof limit === "number" ? matchingFleets.slice(0, limit) : matchingFleets;
  useEffect(() => onResultCount?.(matchingFleets.length), [matchingFleets.length, onResultCount]);
  if (normalizedSearch && !matchingFleets.length) return null;
  return (
    <section className={`delivery-feature ${variant === "home" ? "provider-home-directory" : ""}`}>
      {variant === "directory" && (
        <div className="section-heading">
          <div>
            <h2>{text.title}</h2>
          </div>
        </div>
      )}
      <div className="hotel-directory-grid" hidden={Boolean(selected)}>
        <PaginatedList
          layout="cards"
          items={visibleFleets}
          resetKey={normalizedSearch}
          label="Delivery provider pages"
          renderItem={(fleet, index) => (
            <button
              className="hotel-listing-card"
              type="button"
              aria-haspopup="dialog"
              key={fleet.id}
              onClick={() => {
                setSelected(fleet);
                setLoginRequested(false);
              }}
            >
              <DeliveryArtwork variant={index % 3} />
              <span className="hotel-listing-card__body">
                <strong>{fleet.display_name}</strong>
                <small>{fleet.address}</small>
                <span>{text.help}</span>
                <b>{text.verified}</b>
                <em>
                  {text.request}
                  <span aria-hidden="true">→</span>
                </em>
              </span>
            </button>
          )}
        />
        {!visibleFleets.length && !normalizedSearch && (
          <Card className="empty-state">
            <strong>{text.empty}</strong>
          </Card>
        )}
      </div>
      {selected && (
        <div className="provider-detail-page" role="presentation">
          <section
            className="provider-detail-content"
            role="region"
            aria-labelledby="delivery-detail-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header className="hotel-detail-dialog__header">
              <div>
                <p className="eyebrow">{text.verified}</p>
                <h2 id="delivery-detail-title">{selected.display_name}</h2>
                <p>{selected.address}</p>
              </div>
              <button
                className="service-dialog__close"
                type="button"
                aria-label={text.close}
                onClick={() => setSelected(null)}
              >
                ×
              </button>
            </header>
            <div className="hotel-detail-dialog__content">
              <div className="hotel-detail-hero">
                <DeliveryArtwork variant={Math.max(0, fleets.indexOf(selected)) % 3} />
                <div>
                  <p className="eyebrow">{text.verified}</p>
                </div>
              </div>
              {selected.organization_id && (
                <ProviderReputation key={selected.organization_id} org={selected.organization_id} />
              )}
              <Card className="merchant-order-card">
                <h3>{text.request}</h3>

                {auth.user ? (
                  <DirectDeliveryForm fleet={selected.id} />
                ) : (
                  <button
                    className="button merchant-primary-action"
                    type="button"
                    onClick={() => setLoginRequested(true)}
                  >
                    {text.request}
                  </button>
                )}
              </Card>
              {loginRequested && !auth.user && renderAuthPrompt?.(() => setLoginRequested(false))}
            </div>
          </section>
        </div>
      )}
    </section>
  );
}

function DeliveryArtwork({ variant }: { readonly variant: number }) {
  return (
    <span
      className={`hotel-listing-card__image provider-art provider-art--delivery hotel-listing-card__image--${variant}`}
      aria-hidden="true"
    >
      <span />
      <svg viewBox="0 0 80 64">
        <path d="M10 20h40v27H10zM50 30h12l8 10v7H50zM22 47a7 7 0 1 0 0 .1M59 47a7 7 0 1 0 0 .1M16 29h23" />
      </svg>
      <i />
    </span>
  );
}
export function DeliveryOwnerDispatch({
  view = "dashboard",
}: {
  readonly view?: "dashboard" | "riders" | "orders";
}) {
  const a = useAuth(),
    [fleet, setFleet] = useState<Fleet | null>(null),
    [jobs, setJobs] = useState<Job[]>([]),
    [riders, setRiders] = useState<Rider[]>([]),
    [message, setMessage] = useFeedback(),
    [busyId, setBusyId] = useState("");
  const [riderQuery, setRiderQuery] = useState("");
  const [riderStatus, setRiderStatus] = useState("all");
  const [jobQuery, setJobQuery] = useState("");
  const [jobStatus, setJobStatus] = useState("all");
  const filteredRiders = riders.filter(
    (r) =>
      (riderStatus === "all" ||
        (riderStatus === "paused"
          ? !r.is_active
          : r.is_active && r.is_available === (riderStatus === "available"))) &&
      `${r.display_name} ${r.phone} ${r.vehicle_label}`
        .toLowerCase()
        .includes(riderQuery.trim().toLowerCase()),
  );
  const load = useCallback(async () => {
    if (!a.client || !a.user) return;
    setMessage("");
    const membership = await a.client
      .from("organization_memberships")
      .select("organization_id")
      .eq("user_id", a.user.id)
      .eq("status", "active")
      .eq("role", "delivery_owner");
    if (membership.error) {
      setMessage(membership.error.message);
      return;
    }
    const organizationIds = ((membership.data ?? []) as { organization_id: string }[]).map(
      (m) => m.organization_id,
    );
    if (!organizationIds.length) {
      setFleet(null);
      return;
    }
    const { data: f, error: fleetError } = await a.client
      .from("delivery_fleets")
      .in("organization_id", organizationIds)
      .select("id,display_name,is_active")
      .limit(1)
      .maybeSingle();
    if (fleetError) {
      setMessage(fleetError.message);
      return;
    }
    setFleet((f as Fleet | null) ?? null);
    if (!f) return;
    const [jobResult, riderResult] = await Promise.all([
      a.client
        .from("delivery_jobs")
        .select(
          "id,status,pickup_address,dropoff_address,recipient_name,recipient_phone,cash_to_collect_minor,rider_id",
        )
        .eq("fleet_id", f.id)
        .order("created_at", { ascending: false }),
      a.client
        .from("delivery_riders")
        .select("id,display_name,phone,vehicle_label,is_available,is_active")
        .eq("fleet_id", f.id),
    ]);
    const error = jobResult.error ?? riderResult.error;
    if (error) {
      setMessage(error.message);
      return;
    }
    setJobs((jobResult.data ?? []) as Job[]);
    setRiders((riderResult.data ?? []) as Rider[]);
  }, [a.client, a.user, setMessage]);
  useOrderRefresh(load);
  useEffect(() => {
    const x = setTimeout(() => void load(), 0);
    return () => clearTimeout(x);
  }, [load]);
  async function assign(job: string, rider: string) {
    if (!a.client || !rider) return;
    const { error } = await a.client.rpc("assign_delivery_job", {
      target_job_id: job,
      target_rider_id: rider,
    });
    setMessage(error?.message ?? "Rider assigned and notified.");
    if (!error) await load();
  }
  async function addRider(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!a.client) return;
    const form = event.currentTarget;
    const data = new FormData(form);
    setBusyId("new-rider");
    const { error } = await a.client.rpc("add_delivery_rider_by_email", {
      rider_email: data.get("email"),
      rider_name: data.get("name"),
      rider_phone: data.get("phone"),
      rider_vehicle: data.get("vehicle"),
    });
    setBusyId("");
    setMessage(error?.message ?? "Rider added and notified.");
    if (!error) {
      form.reset();
      form.closest("dialog")?.close();
      await load();
    }
  }
  async function setRiderActive(rider: Rider) {
    if (!a.client) return;
    setBusyId(rider.id);
    const { error } = await a.client.rpc("set_delivery_rider_active", {
      rider_id: rider.id,
      active: !rider.is_active,
    });
    setBusyId("");
    setMessage(error?.message ?? `Rider ${rider.is_active ? "paused" : "activated"}.`);
    if (!error) await load();
  }
  const filteredJobs = useMemo(() => {
    const normalized = jobQuery.trim().toLocaleLowerCase();
    return jobs.filter(
      (job) =>
        (jobStatus === "all" || job.status === jobStatus) &&
        (!normalized ||
          [job.recipient_name, job.recipient_phone, job.pickup_address, job.dropoff_address].some(
            (value) => value.toLocaleLowerCase().includes(normalized),
          )),
    );
  }, [jobQuery, jobStatus, jobs]);
  if (!a.user) return null;
  return (
    <section className="delivery-feature">
      {!fleet && (
        <Card className="empty-state">
          <strong>Your delivery workspace will appear after Admin approval.</strong>
        </Card>
      )}
      {fleet && (
        <>
          <div className="notice">
            {fleet.is_active
              ? `${fleet.display_name} is enabled for dispatch.`
              : `${fleet.display_name} is waiting for Admin activation.`}
          </div>
          {view !== "orders" && (
            <div className="management-grid delivery-management-grid">
              <Card className="management-card rider-roster">
                <h3>Rider roster</h3>
                <ManagementListToolbar
                  actions={
                    <RecordFormDialog title={"Add rider"}>
                      <p className="management-help">
                        The rider must create and verify an account before you add their email.
                      </p>
                      <form className="form-grid" onSubmit={(event) => void addRider(event)}>
                        <label className="field">
                          <span>Verified account email</span>
                          <input name="email" type="email" autoComplete="email" required />
                        </label>
                        <label className="field">
                          <span>Rider name</span>
                          <input name="name" minLength={2} maxLength={120} required />
                        </label>
                        <label className="field">
                          <span>Phone</span>
                          <input name="phone" type="tel" minLength={6} maxLength={30} required />
                        </label>
                        <label className="field">
                          <span>Vehicle</span>
                          <input name="vehicle" minLength={2} maxLength={120} required />
                        </label>
                        <button className="button" disabled={busyId === "new-rider"}>
                          {busyId === "new-rider" ? "Adding…" : "Add rider"}
                        </button>
                      </form>
                    </RecordFormDialog>
                  }
                  query={riderQuery}
                  onQueryChange={setRiderQuery}
                  searchLabel="Search riders"
                  resultCount={filteredRiders.length}
                  filters={[
                    {
                      label: "Status",
                      value: riderStatus,
                      onChange: setRiderStatus,
                      options: ["all", "available", "busy", "paused"].map((value) => ({
                        value,
                        label: value === "all" ? "All statuses" : value,
                      })),
                    },
                  ]}
                  onClear={() => {
                    setRiderQuery("");
                    setRiderStatus("all");
                  }}
                />
                {filteredRiders.length ? (
                  <PaginatedList
                    items={filteredRiders}
                    resetKey={`${riderQuery}:${riderStatus}`}
                    label="Rider roster pages"
                    renderItem={(rider) => (
                      <div className="rider-row" key={rider.id}>
                        <div>
                          <strong>{rider.display_name}</strong>
                          <small>
                            {rider.vehicle_label} · {rider.phone}
                          </small>
                        </div>
                        <StatusPill
                          tone={rider.is_active && rider.is_available ? "success" : "neutral"}
                        >
                          {!rider.is_active ? "Paused" : rider.is_available ? "Available" : "Busy"}
                        </StatusPill>
                        <button
                          className="text-button"
                          disabled={busyId === rider.id}
                          onClick={() => void setRiderActive(rider)}
                        >
                          {rider.is_active ? "Pause" : "Activate"}
                        </button>
                      </div>
                    )}
                  />
                ) : (
                  <PaginatedList items={[]} label="Riders" renderItem={() => null} />
                )}
              </Card>
            </div>
          )}
        </>
      )}
      {view !== "riders" && (
        <>
          <h3 className="queue-title">Dispatch queue</h3>
          <ManagementListToolbar
            query={jobQuery}
            onQueryChange={setJobQuery}
            searchLabel="Search recipient, phone, pickup, or drop-off"
            resultCount={filteredJobs.length}
            filters={[
              {
                label: "Status",
                value: jobStatus,
                onChange: setJobStatus,
                options: [
                  { value: "all", label: "All statuses" },
                  ...Array.from(new Set(jobs.map((job) => job.status))).map((value) => ({
                    value,
                    label: value.replaceAll("_", " "),
                  })),
                ],
              },
            ]}
            onClear={() => {
              setJobQuery("");
              setJobStatus("all");
            }}
          />
          <div className="request-list">
            {filteredJobs.length ? (
              <PaginatedList
                items={filteredJobs}
                resetKey={`${jobQuery}:${jobStatus}`}
                label="Dispatch queue pages"
                renderItem={(j) => (
                  <Card className="request-card" key={j.id}>
                    <div>
                      <StatusPill tone={j.status === "unassigned" ? "warning" : "neutral"}>
                        {j.status}
                      </StatusPill>
                      <h3>{j.recipient_name}</h3>
                      <p>
                        {j.pickup_address} → {j.dropoff_address}
                      </p>
                      <a href={`tel:${j.recipient_phone}`}>{j.recipient_phone}</a>
                    </div>
                    {j.status === "unassigned" && (
                      <select defaultValue="" onChange={(e) => void assign(j.id, e.target.value)}>
                        <option value="" disabled>
                          Assign rider
                        </option>
                        {riders
                          .filter((r) => r.is_active && r.is_available)
                          .map((r) => (
                            <option value={r.id} key={r.id}>
                              {r.display_name}
                            </option>
                          ))}
                      </select>
                    )}
                  </Card>
                )}
              />
            ) : (
              <PaginatedList items={[]} label="Delivery jobs" renderItem={() => null} />
            )}
          </div>
        </>
      )}
      {message && <p className="form-message">{message}</p>}
    </section>
  );
}
export function RiderJobs() {
  const a = useAuth(),
    [rider, setRider] = useState<Rider | null>(null),
    [jobs, setJobs] = useState<Job[]>([]),
    [message, setMessage] = useFeedback(),
    [failureNotes, setFailureNotes] = useState<Record<string, string>>({});
  const [jobQuery, setJobQuery] = useState("");
  const [jobStatus, setJobStatus] = useState("all");
  const load = useCallback(async () => {
    if (!a.client || !a.user) return;
    const { data: r } = await a.client
      .from("delivery_riders")
      .select("id,display_name,phone,vehicle_label,is_available,is_active")
      .eq("user_id", a.user.id)
      .eq("is_active", true)
      .limit(1)
      .maybeSingle();
    setRider((r as Rider | null) ?? null);
    if (!r) return;
    const { data } = await a.client
      .from("delivery_jobs")
      .select(
        "id,status,pickup_address,dropoff_address,recipient_name,recipient_phone,cash_to_collect_minor,rider_id",
      )
      .eq("rider_id", r.id)
      .order("created_at", { ascending: false });
    setJobs((data ?? []) as Job[]);
  }, [a.client, a.user]);
  useOrderRefresh(load);
  useEffect(() => {
    const x = setTimeout(() => void load(), 0);
    return () => clearTimeout(x);
  }, [load]);
  async function next(j: Job, status: string, note = "") {
    if (!a.client) return;
    const cash = status === "delivered" ? j.cash_to_collect_minor : null;
    const { error } = await a.client.rpc("update_delivery_job_status", {
      target_job_id: j.id,
      next_status: status,
      cash_collected: cash,
      note,
    });
    setMessage(error?.message ?? "Status updated.");
    if (!error) await load();
  }
  async function toggleAvailability() {
    if (!a.client || !rider) return;
    const { error } = await a.client.rpc("set_my_delivery_availability", {
      available: !rider.is_available,
    });
    setMessage(error?.message ?? "Availability updated.");
    if (!error) await load();
  }
  const filteredJobs = useMemo(() => {
    const normalized = jobQuery.trim().toLocaleLowerCase();
    return jobs.filter(
      (job) =>
        (jobStatus === "all" || job.status === jobStatus) &&
        (!normalized ||
          [job.recipient_name, job.recipient_phone, job.pickup_address, job.dropoff_address].some(
            (value) => value.toLocaleLowerCase().includes(normalized),
          )),
    );
  }, [jobQuery, jobStatus, jobs]);
  if (!a.user) return null;
  return (
    <section className="delivery-feature">
      <div className="section-heading">
        <div>
          <h2>Active deliveries</h2>
        </div>
      </div>
      {!rider ? (
        <Card className="empty-state">
          <strong>Ask your delivery owner to add this verified account.</strong>
        </Card>
      ) : (
        <div className="account-bar rider-availability">
          <span>
            <strong>{rider.display_name}</strong> · {rider.vehicle_label}
          </span>
          <button className="button button--secondary" onClick={() => void toggleAvailability()}>
            {rider.is_available ? "Go unavailable" : "Go available"}
          </button>
        </div>
      )}
      <ManagementListToolbar
        query={jobQuery}
        onQueryChange={setJobQuery}
        searchLabel="Search my deliveries"
        resultCount={filteredJobs.length}
        filters={[
          {
            label: "Status",
            value: jobStatus,
            onChange: setJobStatus,
            options: [
              { value: "all", label: "All statuses" },
              ...Array.from(new Set(jobs.map((job) => job.status))).map((value) => ({
                value,
                label: value.replaceAll("_", " "),
              })),
            ],
          },
        ]}
        onClear={() => {
          setJobQuery("");
          setJobStatus("all");
        }}
      />
      <div className="request-list">
        <PaginatedList
          items={filteredJobs}
          resetKey={`${jobQuery}:${jobStatus}`}
          label="Rider job pages"
          renderItem={(j) => (
            <Card className="request-card" key={j.id}>
              <div>
                <StatusPill tone="warning">{j.status}</StatusPill>
                <h3>{j.recipient_name}</h3>
                <p>
                  {j.pickup_address} → {j.dropoff_address}
                </p>
                <p>Collect {(j.cash_to_collect_minor / 100).toLocaleString()} MMK</p>
              </div>
              <div className="request-actions">
                {j.status === "assigned" && (
                  <button className="button" onClick={() => void next(j, "accepted")}>
                    Accept
                  </button>
                )}
                {j.status === "accepted" && (
                  <button className="button" onClick={() => void next(j, "picked_up")}>
                    Picked up
                  </button>
                )}
                {j.status === "picked_up" && (
                  <div className="delivery-completion-actions">
                    <button className="button" onClick={() => void next(j, "delivered")}>
                      Delivered
                    </button>
                    <label className="field">
                      <span>Failure reason</span>
                      <input
                        value={failureNotes[j.id] ?? ""}
                        onChange={(event) =>
                          setFailureNotes({ ...failureNotes, [j.id]: event.target.value })
                        }
                        placeholder="Required only when delivery fails"
                      />
                    </label>
                    <button
                      className="button button--danger"
                      disabled={(failureNotes[j.id] ?? "").trim().length < 3}
                      onClick={() => void next(j, "failed", failureNotes[j.id])}
                    >
                      Failed
                    </button>
                  </div>
                )}
              </div>
            </Card>
          )}
        />
        {rider && !jobs.length && (
          <Card className="empty-state">
            <strong>No assigned deliveries.</strong>
          </Card>
        )}
      </div>
      {message && <p className="form-message">{message}</p>}
    </section>
  );
}

export { DirectDeliveryOrders, CustomerDeliveryRequest } from "./direct-delivery";
